import React, { useCallback, useMemo, useState } from 'react';
import { Box, Grid, SearchInput, useDebounce, VirtualTable } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import { useIsEmployerIsPresent, useShowAppliedSettingsSnackbar } from '../hooks';
import PlaceholderBase from '../PlaceholderBase';
import PlanYearSelector from '../PlanYearSelector';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import ReportTableWrapper from '../ReportTable/ReportTableWrapper';
import ToggleSSNVisible from '../ToggleSSNVisible';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import { ForfeitureReportTableParams } from './ForfeitureReport.types';
import ForfeitureSidePicker from './FortifureSidePicker';
import { useForfeitureReportExportCSVQuery, useForfeitureReportQuery } from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: ForfeitureReportTableParams;
  onSetFilterValue: FilterSetters<ForfeitureReportTableParams>;
}

const ForfeitureReportTable: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues);
  const [isAutoFocus, setAutoFocus] = useState(false);
  const debouncedSearch = useDebounce(filtersValues.searchString);
  const {
    isHasNextPage,
    total,
    isLoading,
    headers,
    nodes,
    lastTimeUpdateDate,
    columnWidths,
    handleLoadMore,
    isExportDisabled,
    onChangeHeaderSize,
  } = useForfeitureReportQuery({
    ...filtersValues,
    searchString: debouncedSearch,
  });
  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }

    if (!isLoading && !total) {
      if (filtersValues.searchString.trim()) {
        return (
          <PlaceholderBase isLarge>
            <PlaceholderBase.Search />
          </PlaceholderBase>
        );
      }
      return (
        <PlaceholderBase isLarge>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }
    return (
      <VirtualTable
        testId="report-table"
        height={isPreviewMode ? 'calc(100vh - 545px)' : 'calc(80vh - 160px)'}
        isLoading={isLoading}
        onResizeHeaders={onChangeHeaderSize}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        columnSizes={columnWidths}
        isIndexMode={true}
        headers={headers}
        items={nodes}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtersValues.employerIds,
    isPreviewMode,
    onChangeHeaderSize,
    columnWidths,
    filtersValues.employerId,
    filtersValues.searchString,
    handleLoadMore,
    headers,
    isHasNextPage,
    isLoading,
    nodes,
    total,
  ]);
  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useForfeitureReportExportCSVQuery(filtersValues);

  const handleShowAppliedFilterSnackbar = useShowAppliedSettingsSnackbar();
  const handleToggleSSN = useCallback(
    (value: boolean) => {
      onSetFilterValue.isSSNShowed(value);
      handleShowAppliedFilterSnackbar();
    },
    [handleShowAppliedFilterSnackbar, onSetFilterValue]
  );

  return (
    <Box direction="column">
      <TopButtonPortalWrapper
        isPreviewMode={isPreviewMode}
        infoNode={
          <ReportLastUpdateDate
            isWideMode
            isLoading={isLoading}
            key={`${filtersValues.employerId}`}
            value={lastTimeUpdateDate}
          >
            <Box margin={{ left: 'spacing16' }}>
              <ForfeitureSidePicker
                disabled={!isEmployerIsPresent}
                value={filtersValues.forfeitureGroup}
                onChangeValue={onSetFilterValue.forfeitureGroup}
              />
            </Box>
          </ReportLastUpdateDate>
        }
        buttonNode={
          <Box direction="row">
            <AddScheduledReportButton
              employerId={filtersValues.employerId}
              type={ReportTypeKeys.Forfeiture}
              tab={ReportTab.Report}
            />
            <Box margin={{ left: 'spacing12' }}>
              <ExportButton
                isLoading={isExportLoading}
                onClick={handleExportCSV}
                disabled={!isEmployerIsPresent || isExportDisabled}
                text="Download Report (CSV)"
              />
            </Box>
          </Box>
        }
      >
        <ReportTableWrapper
          total={total}
          isLoading={isLoading}
          isPreviewMode={isPreviewMode}
          previewHeaderChildren={
            <ForfeitureSidePicker
              disabled={!isEmployerIsPresent}
              value={filtersValues.forfeitureGroup}
              onChangeValue={onSetFilterValue.forfeitureGroup}
            />
          }
          headerChildren={
            <ToggleSSNVisible
              disabled={!isEmployerIsPresent}
              value={filtersValues.isSSNShowed}
              onChangeValue={handleToggleSSN}
            />
          }
          isSmallFilterMode
          filters={
            <Box direction="row" fill>
              <Grid
                columns={
                  isPreviewMode
                    ? {
                        count: 3,
                        size: 'auto',
                      }
                    : ['220px', '220px', '220px']
                }
                gap="spacing12"
              >
                {!isPreviewMode && (
                  <SearchInput
                    testId="report"
                    placeholder="Search"
                    value={filtersValues.searchString}
                    disabled={!isEmployerIsPresent}
                    onChange={onSetFilterValue.searchString}
                  />
                )}
                <EmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  value={filtersValues.employerId}
                  onChangeValue={onSetFilterValue.employerId}
                />
                <PlanYearSelector
                  disabled={!isEmployerIsPresent}
                  value={filtersValues.planYearPeriod}
                  onChangeValue={onSetFilterValue.planYearPeriod}
                />
              </Grid>
            </Box>
          }
        >
          <Box key={filtersValues.forfeitureGroup}>{childrenNode}</Box>
        </ReportTableWrapper>
      </TopButtonPortalWrapper>
    </Box>
  );
};
export default ForfeitureReportTable;
